/**
 * "AI integration (MCP)" — the primary integration path, for either scope.
 *
 * An operator mints a token here; the token in the connection string is the
 * ENTIRE configuration an AI agent needs. The raw value appears exactly once
 * (the create response — the same show-once contract as the instance secret);
 * afterwards only label / created / last-used are ever shown, which is also how
 * an operator can tell whether an agent is actually using its token.
 *
 * The component is scope-agnostic: the config editor passes the project-token
 * calls, the organization editor passes the org-token calls. One implementation,
 * because the show-once handling and the per-harness commands are the parts that
 * must not quietly diverge between the two screens.
 */
import { useState, type ReactElement } from 'react';

import type { CreatedMcpToken, McpToken } from '@hotline/contracts';
import { harnessCommands, mcpConnectionString } from '@hotline/integration-kit';

import { API_BASE_URL } from '../config.js';
import { useAsync, toMessage } from '../lib/useAsync.js';

import { CopyBlock, ErrorBanner } from './ui.js';
import { Button, Input } from './ui/index.js';

/**
 * Stands in for the real connection string in the always-visible commands. It is
 * deliberately obvious rather than realistic — a plausible-looking fake token is
 * the kind of thing that gets pasted and then debugged for twenty minutes.
 */
const CONNECTION_PLACEHOLDER = `${API_BASE_URL.replace(/\/+$/, '')}/mcp/<your-token>`;

function formatWhen(iso: string | null): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleString();
}

/** The three calls that differ between a project scope and an organization scope. */
export interface McpTokenApi {
  /** Stable key for the resource these tokens belong to (re-fetch trigger). */
  scopeKey: string;
  list: () => Promise<McpToken[]>;
  create: (label: string) => Promise<CreatedMcpToken>;
  revoke: (tokenId: string) => Promise<void>;
}

export interface McpSectionProps {
  api: McpTokenApi;
  /** What an agent holding this token can reach — shown above the controls. */
  blurb: string;
}

export function McpSection({ api, blurb }: McpSectionProps): ReactElement {
  const tokens = useAsync<McpToken[]>(() => api.list(), [api.scopeKey]);

  const [label, setLabel] = useState('');
  const [minted, setMinted] = useState<CreatedMcpToken | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.create(label.trim());
      setMinted(created);
      setLabel('');
      await tokens.reload();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (tokenId: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.revoke(tokenId);
      if (minted?.id === tokenId) setMinted(null);
      await tokens.reload();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const connection = minted ? mcpConnectionString(API_BASE_URL, minted.token) : null;
  const live = (tokens.data ?? []).filter((t) => !t.revokedAt);

  return (
    <div className="flex flex-col gap-4 py-3.5">
      <p className="text-[13px] text-muted">{blurb}</p>

      {error ? <ErrorBanner message={error} /> : null}

      {/* Mint */}
      <div className="flex items-center gap-2">
        <Input
          aria-label="Token label"
          placeholder='Label (e.g. "Alexey’s laptop", "CI agent")'
          className="flex-1 text-[13px]"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={busy}
        />
        <Button type="button" variant="secondary" size="sm" onClick={() => void create()} disabled={busy}>
          Generate token
        </Button>
      </div>

      {/* Show-once reveal — the ONE moment the real token exists in the UI. */}
      {minted && connection ? (
        <div className="flex flex-col gap-3 rounded-md border border-warn/40 bg-warn-soft/30 p-3">
          <p className="text-[12px] font-semibold text-warn" role="alert">
            Copy this now — the token is shown only once. Treat it like a password: it rides
            in the URL, so anyone holding the connection string can use these tools.
          </p>
          <div data-testid="mcp-connection-string">
            <CopyBlock label="Connection string" code={connection} language="text" />
          </div>
        </div>
      ) : null}

      {/*
        How to connect, always visible. The commands are the same either way —
        only the string differs — so an operator can read what the integration
        involves, and hand the steps to a developer, BEFORE deciding to mint a
        credential. Rendering them only at mint time meant the instructions
        vanished forever the moment the dialog was dismissed.
      */}
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="text-[13px] font-semibold text-text">Add it to your agent</h3>
          <p className="mt-[3px] text-[12.5px] leading-relaxed text-muted">
            {minted
              ? 'Run the line for your agent — the connection string above is already filled in.'
              : 'Generate a token above, then run the line for your agent with the connection string it gives you. Nothing else to configure: no headers, no environment variables.'}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {harnessCommands(connection ?? CONNECTION_PLACEHOLDER).map((cmd) => (
            <div key={cmd.id} data-testid={`mcp-harness-${cmd.id}`}>
              <CopyBlock
                label={cmd.hint ? `${cmd.label} — ${cmd.hint}` : cmd.label}
                code={cmd.code}
                language={cmd.language}
              />
            </div>
          ))}
        </div>
        <p className="text-[11.5px] text-faint">
          <code className="font-mono">?lang=</code> on the connection string is reserved for
          localized tool output; English is served today.
        </p>
      </div>

      {/* Existing tokens */}
      {tokens.loading ? <p className="text-[12.5px] text-muted">Loading tokens…</p> : null}
      {live.length > 0 ? (
        <ul className="flex flex-col gap-2" data-testid="mcp-token-list">
          {live.map((t) => (
            <li
              key={t.id}
              className="flex items-center gap-3 rounded-md border border-border bg-surface-2 px-3 py-2 text-[12.5px]"
            >
              <span className="min-w-0 flex-1 truncate font-semibold text-text">
                {t.label || 'Unlabelled token'}
              </span>
              <span className="shrink-0 text-faint">created {formatWhen(t.createdAt)}</span>
              <span className="shrink-0 text-faint">· last used {formatWhen(t.lastUsedAt)}</span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => void revoke(t.id)}
                disabled={busy}
                aria-label={`Revoke ${t.label || 'token'}`}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      ) : !tokens.loading && !minted ? (
        <p className="text-[12.5px] text-faint">
          No tokens yet. Generate one and hand the connection string to your agent.
        </p>
      ) : null}
    </div>
  );
}
